import { useEffect } from 'react';

import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';

import * as Sentry from '@sentry/nextjs';
import { SWRConfig } from 'swr';
// import EpnUIConfig from 'epn-ui/umd/dist/components/EpnUIConfig';
import EpnUiConfigProvider from 'epn-ui/umd/dist/components/EpnUiConfigProvider';
// import EpnTheme from 'epn-ui/umd/dist/configs/EpnTheme.json';
// import ReactGA from 'react-ga';
import { config } from '@fortawesome/fontawesome-svg-core';
import { AmplitudeClient } from 'amplitude-js';
import 'core-js/features/global-this';
import 'core-js/features/observable';
import RefManager from 'epn-ref-manager';
import UTM, { CookieSaveClient } from 'epn-utm-params-manager';
import 'intersection-observer';
import { Zoom } from 'react-toastify';

import '@fortawesome/fontawesome-svg-core/styles.css';
import useI18n from '@i18n';
import 'react-toastify/dist/ReactToastify.css';
import '../assets/styles/main.scss';

import apiConfig from '@config/api.json';
// import gaConfig from '@config/ga.json';
import REF_CONFIG from '@config/ref';
import UTM_CONFIG from '@config/utm';

import { CloseButtonIcon, StyledToastContainer } from '@base/BaseToast';

import { getLayoutByPathname } from '@utils/layouts';
import {
    AMPLITUDE_EVENTS_KEYS,
    APLITUDE_SESSION_STORAGE_KEY,
    initAmplitude,
    logAmplitudeEvent,
    setAmplitudeUserProperties,
} from 'src/utils/metrics/amplitude';

import BaseWithRole from '@components/base/BaseWithRole';

// Modals
import { BasePopupsManager } from '@components/base/BasePopupsManager';

import 'epn-ui/umd/dist/index.css';
import '../tailwind.css';
// TODO вынести в epn-ui
import '../styles/epn-ui/chips.css';
import '../styles/epn-ui/grid.css';
import '../styles/epn-ui/modal.css';
import '../styles/epn-ui/tooltip.css';

import ErrorBoundary from '@components/base/BaseErrorBoundary';
import useRefreshToken from '@hooks/useRefreshToken';
import EpnTheme from '../config/epn_theme.json';

config.autoAddCss = false;

// Google analytics
// if (process.env.NODE_ENV === 'production' && process.browser) {
//     ReactGA.initialize(gaConfig.gaId, {
//         gaOptions: {
//             allowLinker: true,
//         },
//     });
// }

// Amplitude
const AMPLITUDE: AmplitudeClient | null = initAmplitude();

// Обработка utm параметров
let utmManager: UTM | null = null;
// Обработка ref параметров
let refManager: RefManager | null = null;

if (typeof window !== 'undefined') {
    const utmSaveClient = new CookieSaveClient(UTM_CONFIG.cookies);
    utmManager = new UTM(utmSaveClient);
    refManager = new RefManager(REF_CONFIG, UTM_CONFIG.cookies);
}

const MyApp: React.FC<AppProps> = ({ Component, pageProps }) => {
    const { t, locale } = useI18n();
    const isAuth = useRefreshToken([Component]);
    const router = useRouter();
    const { pathname } = router;
    const Layout = getLayoutByPathname(pathname);

    // TODO Убрать условие на epn и сделать красиво
    const showLiveChat = isAuth && process.env.NEXT_PUBLIC_PROJECT_NAME === 'epn';
    const addNoindexMeta = process.env.NEXT_PUBLIC_PROJECT_NAME !== 'epn';

    useEffect(() => {
        if (!isAuth) {
            if (utmManager instanceof UTM) {
                const params = utmManager.parse(new URL(window.location.href));
                const areUTMTagsExist = Array.isArray(params) && params.length;

                if (areUTMTagsExist) {
                    utmManager.save(params);
                }
                if (areUTMTagsExist && !document.referrer) {
                    utmManager.saveReferrer(null);
                } else {
                    utmManager.saveReferrer(document.referrer);
                }
            }
            if (refManager instanceof RefManager) {
                refManager.getClickID();
            }
        }
    }, [isAuth]);

    useEffect(() => {
        if (AMPLITUDE !== null) {
            setAmplitudeUserProperties({
                LOCALE: locale,
            });

            // Логирование начала сессии
            const sessionId = AMPLITUDE.getSessionId().toString();
            const currentSessionId = localStorage.getItem(APLITUDE_SESSION_STORAGE_KEY);

            if (!currentSessionId || sessionId !== currentSessionId) {
                localStorage.setItem(APLITUDE_SESSION_STORAGE_KEY, sessionId);
            }

            if (sessionId !== currentSessionId) {
                logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.sessionStart);
            }
        }
    }, [locale]);

    return (
        <>
            <Head>
                <title>{t('Affiliate account')}</title>
                <meta name="description" content={t('Cabinet WM')} />
                {addNoindexMeta && <meta name="robots" content="noindex" />}
            </Head>
            {showLiveChat && (
                <Script strategy="lazyOnload" src="//code.jivo.ru/widget/GBeMVuWTJ8" />
            )}
            <EpnUiConfigProvider theme={EpnTheme} locale={locale}>
                {/* TODO: положить fetcher сюда */}
                <SWRConfig
                    value={{
                        revalidateOnFocus: false,
                        onError: (error, key) => {
                            if (!apiConfig.SWR_STOP_ERORR_CODES.includes(error.status)) {
                                const requestId = error?.response?.headers?.get('request-id');

                                Sentry.captureException(error, {
                                    tags: {
                                        key,
                                    },
                                    extra: {
                                        requestId,
                                    },
                                });
                            }
                        },
                    }}
                >
                    <Layout isAuth={isAuth}>
                        <ErrorBoundary>
                            <BaseWithRole Component={Component} locale={locale} props={pageProps} />
                            {isAuth && <BasePopupsManager />}
                        </ErrorBoundary>
                    </Layout>
                </SWRConfig>
            </EpnUiConfigProvider>
            <StyledToastContainer
                transition={Zoom}
                autoClose={3000}
                closeButton={<CloseButtonIcon />}
            />
        </>
    );
};

export default MyApp;
